/**
 * 리드 API (Sprint 2 — 레거시).
 *
 * 딜(deal-api.ts) 도입 이전의 lead/lead_item 흐름. LeadCreateView/LeadListView 가 사용.
 */

import { z } from 'zod';
import type { CustomerRow } from './partner-api';
import { API_BASE, authHeaders, getJson, postJson, parseResponse, httpError } from './http';

// ─── Lead (Sprint 2) ───────────────────────────────────────────────────────
// 레거시 흐름 — 응답이 raw row 덩어리라 핵심 필드만 고정하는 느슨한 경계 스키마(파일 로컬).

export type LeadStatus =
  | 'inquiry'
  | 'quoted'
  | 'delivered'
  | 'paid'
  | 'closed'
  | 'lost';

export type InquiryChannel = 'homepage' | 'kakao' | 'email' | 'phone' | 'other';

export type LeadCategory =
  | 'chassis'
  | 'cpu'
  | 'gpu'
  | 'memory'
  | 'ssd'
  | 'hdd'
  | 'network_adapter'
  | 'hba'
  | 'raid_controller'
  | 'power_supply'
  | 'pre_configured_model'
  | 'other';

export interface LeadInput {
  customer_id: number;
  customer_contact?: string | null;
  inquiry_channel: InquiryChannel;
  status?: LeadStatus;
  inquiry_summary?: string | null;
  notes?: string | null;
  assigned_to?: string | null;
  expected_close_date?: string | null; // 'YYYY-MM-DD'
  created_by?: string | null;
}

export interface LeadItemInput {
  category?: LeadCategory | null;
  option_part?: string | null;
  item_description?: string | null;
  quantity?: number | null;
  target_unit_price_usd?: number | null;
  /** RFQ 발송 대상 여부 — 시트의 토글 컬럼. default false. */
  needs_rfq?: boolean;
  notes?: string | null;
  sort_order?: number;
}

export interface LeadBulkResponse {
  lead: Record<string, unknown> & { id: number };
  items: Record<string, unknown>[];
}

const LeadBulkResponseSchema: z.ZodType<LeadBulkResponse> = z.object({
  lead:  z.object({ id: z.coerce.number() }).catchall(z.unknown()),
  items: z.array(z.record(z.string(), z.unknown())),
});

/** POST /api/admin/leads/bulk — lead + lead_item 들 트랜잭션 일괄 생성. */
export async function createLeadBulk(body: {
  lead: LeadInput;
  items: LeadItemInput[];
}): Promise<LeadBulkResponse> {
  return postJson<LeadBulkResponse>('/api/admin/leads/bulk', body, LeadBulkResponseSchema);
}

export interface LeadFullResponse {
  lead: Record<string, unknown> & { id: number };
  customer: CustomerRow | null;
  items: Record<string, unknown>[];
  rfqs: Record<string, unknown>[];
}

const LeadFullResponseSchema: z.ZodType<LeadFullResponse> = z.object({
  lead:     z.object({ id: z.coerce.number() }).catchall(z.unknown()),
  customer: z.custom<CustomerRow | null>(() => true),   // 레거시 — partner 스키마 확정 후 교체
  items:    z.array(z.record(z.string(), z.unknown())),
  rfqs:     z.array(z.record(z.string(), z.unknown())),
});

/** GET /api/admin/leads/:id/full — 상세 페이지용 */
export async function fetchLeadFull(id: number | string): Promise<LeadFullResponse> {
  return getJson<LeadFullResponse>(
    `/api/admin/leads/${encodeURIComponent(String(id))}/full`,
    { schema: LeadFullResponseSchema },
  );
}

/**
 * PUT /api/admin/leads/:id/full — 리드 + 품목 일괄 수정 (diff sync).
 *
 * items[i].id 있는 행 → UPDATE, 없는 행 → INSERT,
 * 기존 활성 행 중 body 에 없는 것 → soft delete (is_active=FALSE).
 *
 * 응답은 GET /full 과 동일한 구조 — 새로 발급된 lead_item.id 까지 포함.
 */
export async function updateLeadFull(
  id: number | string,
  body: {
    lead: Partial<LeadInput>;
    items: Array<LeadItemInput & { id?: number }>;
  },
): Promise<LeadFullResponse> {
  const res = await fetch(
    `${API_BASE}/api/admin/leads/${encodeURIComponent(String(id))}/full`,
    {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    let detail = '';
    try {
      const j = (await res.json()) as { error?: string; detail?: string };
      detail = j.error || j.detail || '';
    } catch {
      /* ignore */
    }
    throw new Error(`HTTP ${res.status} ${detail}`.trim());
  }
  return parseResponse(await res.json(), LeadFullResponseSchema, 'PUT /api/admin/leads/:id/full');
}

// {row} 형태 단건 응답 (updateLead/updateLeadItem 공용)
const RowResponseSchema = z.object({ row: z.record(z.string(), z.unknown()) });

/** PATCH /api/admin/lead/:id — 상세 페이지 인라인 편집 */
export async function updateLead(
  id: number | string,
  patch: Partial<LeadInput>,
): Promise<Record<string, unknown>> {
  const res = await fetch(`${API_BASE}/api/admin/lead/${encodeURIComponent(String(id))}`, {
    method: 'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    throw await httpError(res);
  }
  const json = parseResponse(await res.json(), RowResponseSchema, 'PATCH /api/admin/lead/:id');
  return json.row;
}

const RowOrRowsResponseSchema = z.union([
  z.object({ row: z.record(z.string(), z.unknown()) }),
  z.object({ rows: z.array(z.record(z.string(), z.unknown())) }),
]);

/** POST /api/admin/lead_item — 상세 페이지에서 단일 행 추가 */
export async function createLeadItem(
  input: LeadItemInput & { lead_id: number },
): Promise<Record<string, unknown>> {
  const res = await postJson<
    { row: Record<string, unknown> } | { rows: Record<string, unknown>[] }
  >('/api/admin/lead_item', input, RowOrRowsResponseSchema);
  const row = 'row' in res ? res.row : res.rows[0];
  if (!row) throw new Error('lead_item 생성 응답에 row 가 없습니다');
  return row;
}

/** PATCH /api/admin/lead_item/:id */
export async function updateLeadItem(
  id: number | string,
  patch: Partial<LeadItemInput>,
): Promise<Record<string, unknown>> {
  const res = await fetch(
    `${API_BASE}/api/admin/lead_item/${encodeURIComponent(String(id))}`,
    {
      method: 'PATCH',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) {
    throw await httpError(res);
  }
  const json = parseResponse(await res.json(), RowResponseSchema, 'PATCH /api/admin/lead_item/:id');
  return json.row;
}

/** DELETE /api/admin/lead_item/:id */
export async function deleteLeadItem(id: number | string): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/admin/lead_item/${encodeURIComponent(String(id))}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  if (!res.ok) {
    throw await httpError(res);
  }
}
